/**
 * AiCloud Landing Page — /aicloud
 * Dynamic stats, CSS variables, Lucide icons
 */
import axios from 'axios'
import {
    ArrowRight, BarChart2, Bot, Check, Cloud,
    Code2, Globe, Key, Lock, Monitor,
    Network, Server, Shield, Terminal, Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL } from '../config/api'
import './Landing.css'

interface PublicStats {
  user_count: number
  deployment_count: number
  uptime_percentage: number
  agent_count?: number
  skill_count?: number
}

const PROVIDERS = [
  { name: 'Cloudflare', color: '#f6821f', icon: <Cloud size={18} />, features: ['Workers AI — 89 models', 'D1 Database', 'R2 Storage', 'KV Store', 'Vectorize', 'AI Gateway'] },
  { name: 'Azure', color: '#0078d4', icon: <Server size={18} />, features: ['Azure OpenAI', 'Cognitive Services', 'ML Studio', 'Functions', 'Blob Storage'] },
  { name: 'AWS', color: '#ff9900', icon: <Globe size={18} />, features: ['Bedrock', 'SageMaker', 'Lambda', 'S3', 'DynamoDB'] },
  { name: 'GCP', color: '#4285f4', icon: <BarChart2 size={18} />, features: ['Vertex AI', 'Gemini API', 'Cloud Run', 'BigQuery', 'Cloud Storage'] },
]

const FEATURES = [
  { icon: <Key size={18} />, color: '#6366f1', title: 'Your API Keys', desc: 'Connect your own provider keys. We encrypt them with AES-256 — never stored in plaintext.' },
  { icon: <Lock size={18} />, color: '#ec4899', title: 'Zero Lock-In', desc: 'Switch providers anytime. Your agents, your data, your infrastructure.' },
  { icon: <Network size={18} />, color: '#10b981', title: 'Edge Inference', desc: 'Run models at the edge with Cloudflare Workers AI. 300+ global locations, 0 cold start.' },
  { icon: <Bot size={18} />, color: '#f59e0b', title: '512 Agents', desc: 'Platform agents use your cloud. User agents use their own keys. Full isolation.' },
  { icon: <Terminal size={18} />, color: '#06b6d4', title: 'CLI First', desc: 'Deploy agents, run skills, manage cloud — all from your terminal with aicloud CLI.' },
  { icon: <Shield size={18} />, color: '#a855f7', title: 'Audit Logs', desc: 'Every API call logged. Know exactly what your agents are doing and spending.' },
]

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: '/month',
    color: '#6b7280',
    features: ['3 agents', '5 skills', '1 cloud provider', '1,000 API calls/month', 'Community support'],
    cta: 'Get started',
    ctaTo: '/register',
    featured: false,
  },
  {
    name: 'Pro',
    price: '$29',
    period: '/month',
    color: '#6366f1',
    features: ['Unlimited agents', 'All skills', '6 cloud providers', '100,000 API calls/month', 'Priority support', 'FLZ earnings', 'Custom agent personas'],
    cta: 'Start Pro',
    ctaTo: '/register',
    featured: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    color: '#f6821f',
    features: ['Everything in Pro', 'Dedicated infrastructure', 'SLA guarantee', 'Custom integrations', 'Dedicated support', 'On-premise option'],
    cta: 'Contact us',
    ctaTo: '/contact',
    featured: false,
  },
]

export default function AiCloud() {
  const [stats, setStats] = useState<PublicStats>({ user_count: 0, deployment_count: 0, uptime_percentage: 99.9, agent_count: 512, skill_count: 89 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      const [pubR, platR] = await Promise.allSettled([
        axios.get(`${API_URL}/api/public/stats`),
        axios.get(`${API_URL}/api/platform/stats`),
      ])
      const toVal = <T,>(d: unknown, ...keys: string[]): Partial<T> => {
        if (d && typeof d === 'object') {
          for (const k of keys) {
            const v = (d as Record<string, unknown>)[k]
            if (v !== undefined) {return v as Partial<T>}
          }
          return d as Partial<T>
        }
        return {}
      }
      if (pubR.status === 'fulfilled') {
        setStats(s => ({ ...s, ...toVal<PublicStats>(pubR.value.data) }))
      }
      if (platR.status === 'fulfilled') {
        const d = platR.value.data as { total_agents?: number; total_skills?: number }
        setStats(s => ({ ...s, agent_count: d?.total_agents ?? s.agent_count, skill_count: d?.total_skills ?? s.skill_count }))
      }
      setLoading(false)
    }
    void load()
  }, [])

  return (
    <MainLayout>
      <div className="ac-root">

        {/* HERO */}
        <section className="ac-hero">
          <div className="ac-hero-glow ac-glow-1" />
          <div className="ac-hero-glow ac-glow-2" />
          <div className="ac-container ac-hero-inner">
            <div className="ac-hero-badge">
              <Zap size={13} /> Multi-Cloud AI Infrastructure
            </div>
            <h1 className="ac-hero-title">
              Your AI infrastructure.<br />
              <span className="ac-gradient-text">Your rules.</span>
            </h1>
            <p className="ac-hero-sub">
              Deploy 512 AI agents to your own cloud. Full data ownership, zero vendor lock-in, 6 cloud providers, 89+ AI models.
            </p>
            <div className="ac-hero-btns">
              <Link to="/register" className="ac-btn-primary">
                Get started free <ArrowRight size={15} />
              </Link>
              <Link to="/dashboard" className="ac-btn-ghost">
                <Monitor size={14} /> Open Dashboard
              </Link>
            </div>

            {/* Inline stats */}
            <div className="ac-hero-stats">
              {[
                { v: loading ? '—' : `${stats.user_count || 328}+`, l: 'Users' },
                { v: loading ? '—' : `${stats.agent_count || 512}`, l: 'AI Agents' },
                { v: loading ? '—' : `${stats.skill_count || 89}+`, l: 'Skills' },
                { v: `${stats.uptime_percentage}%`, l: 'Uptime' },
              ].map(s => (
                <div key={s.l} className="ac-hero-stat">
                  <div className="ac-hero-stat-v">{s.v}</div>
                  <div className="ac-hero-stat-l">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROVIDERS */}
        <section className="ac-section">
          <div className="ac-container">
            <div className="ac-section-head">
              <div className="ac-badge">Multi-Cloud</div>
              <h2 className="ac-section-title">Connect any cloud</h2>
              <p className="ac-section-sub">One platform, six providers. Mix and match for cost, performance, and compliance.</p>
            </div>
            <div className="ac-providers-grid">
              {PROVIDERS.map(p => (
                <div key={p.name} className="ac-provider-card">
                  <div className="ac-provider-header">
                    <div className="ac-provider-icon" style={{ background: `${p.color}18`, color: p.color }}>
                      {p.icon}
                    </div>
                    <div className="ac-provider-name">{p.name}</div>
                    <div className="ac-provider-dot" style={{ background: p.color }} />
                  </div>
                  <div className="ac-provider-features">
                    {p.features.map(f => (
                      <div key={f} className="ac-provider-feature">
                        <Check size={11} color="#10b981" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/aicloud/app/platforms/cloudflare" className="ac-provider-link">
                    Connect <ArrowRight size={12} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="ac-section ac-section-alt">
          <div className="ac-container">
            <div className="ac-section-head">
              <div className="ac-badge">Platform</div>
              <h2 className="ac-section-title">Built for serious AI work</h2>
              <p className="ac-section-sub">Security, flexibility, and scale — without the complexity.</p>
            </div>
            <div className="ac-features-grid">
              {FEATURES.map(f => (
                <div key={f.title} className="ac-feature-card">
                  <div className="ac-feature-icon" style={{ background: `${f.color}15`, color: f.color }}>
                    {f.icon}
                  </div>
                  <div className="ac-feature-title">{f.title}</div>
                  <div className="ac-feature-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="ac-section">
          <div className="ac-container">
            <div className="ac-section-head">
              <div className="ac-badge">Setup</div>
              <h2 className="ac-section-title">Up and running in 3 steps</h2>
            </div>
            <div className="ac-steps">
              {[
                { n: '1', color: '#6366f1', icon: <Key size={20} />, title: 'Connect your cloud', desc: 'Add your API keys for Cloudflare, Azure, AWS, or GCP. Encrypted and isolated per user.' },
                { n: '2', color: '#ec4899', icon: <Bot size={20} />, title: 'Create your agents', desc: 'Choose from 512 platform agents or create your own. Assign LLM, persona, and capabilities.' },
                { n: '3', color: '#10b981', icon: <Zap size={20} />, title: 'Run skills & workflows', desc: 'Install skills from the marketplace. Chain them into workflows. Deploy to your cloud.' },
              ].map(step => (
                <div key={step.n} className="ac-step">
                  <div className="ac-step-num" style={{ background: `${step.color}15`, color: step.color }}>{step.n}</div>
                  <div className="ac-step-icon" style={{ background: `${step.color}12`, color: step.color }}>{step.icon}</div>
                  <div className="ac-step-title">{step.title}</div>
                  <div className="ac-step-desc">{step.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLI */}
        <section className="ac-section ac-section-alt">
          <div className="ac-container ac-cli-wrap">
            <div className="ac-cli-left">
              <div className="ac-badge">CLI</div>
              <h2 className="ac-section-title" style={{ textAlign: 'left', marginBottom: 12 }}>
                Manage everything from your terminal
              </h2>
              <p className="ac-section-sub" style={{ textAlign: 'left', margin: 0, marginBottom: 24 }}>
                Deploy agents, run skills, explore cloud resources — all with one CLI.
              </p>
              <Link to="/aicloud/app/cli" className="ac-btn-primary" style={{ width: 'fit-content' }}>
                <Terminal size={14} /> Download CLI
              </Link>
            </div>
            <div className="ac-cli-box">
              <div className="ac-cli-bar">
                <span className="ac-cli-dot" style={{ background: '#ff5f57' }} />
                <span className="ac-cli-dot" style={{ background: '#febc2e' }} />
                <span className="ac-cli-dot" style={{ background: '#28c840' }} />
                <span className="ac-cli-title">Terminal</span>
              </div>
              {[
                { prompt: '$', cmd: 'aicloud agents list', out: '✓ 512 agents active' },
                { prompt: '$', cmd: 'aicloud skills run web-scrape --url https://...', out: '✓ Scraped 47 pages' },
                { prompt: '$', cmd: 'aicloud deploy --provider cloudflare', out: '✓ Deployed to Workers AI' },
                { prompt: '$', cmd: 'aicloud research start "AI trends 2026"', out: '✓ Research started — 3 agents assigned' },
              ].map((line, i) => (
                <div key={i} className="ac-cli-line">
                  <span className="ac-cli-prompt">{line.prompt}</span>
                  <span className="ac-cli-cmd">{line.cmd}</span>
                  <div className="ac-cli-out">{line.out}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="ac-section">
          <div className="ac-container">
            <div className="ac-section-head">
              <div className="ac-badge">Pricing</div>
              <h2 className="ac-section-title">Simple, transparent pricing</h2>
              <p className="ac-section-sub">Start free. Scale when you&apos;re ready.</p>
            </div>
            <div className="ac-plans-grid">
              {PLANS.map(plan => (
                <div key={plan.name} className={`ac-plan-card${plan.featured ? ' ac-plan-featured' : ''}`}>
                  {plan.featured && <div className="ac-plan-badge">Most Popular</div>}
                  <div className="ac-plan-name" style={{ color: plan.color }}>{plan.name}</div>
                  <div className="ac-plan-price">
                    <span className="ac-plan-amount">{plan.price}</span>
                    <span className="ac-plan-period">{plan.period}</span>
                  </div>
                  <div className="ac-plan-features">
                    {plan.features.map(f => (
                      <div key={f} className="ac-plan-feature">
                        <Check size={13} color={plan.featured ? plan.color : '#10b981'} />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={plan.ctaTo}
                    className={plan.featured ? 'ac-btn-primary' : 'ac-btn-ghost'}
                    style={{ textAlign: 'center', justifyContent: 'center' }}
                  >
                    {plan.cta}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="ac-cta">
          <div className="ac-cta-glow" />
          <div className="ac-container ac-cta-inner">
            <div className="ac-cta-icons">
              <Code2 size={28} color="#6366f1" />
              <Bot size={28} color="#ec4899" />
              <Cloud size={28} color="#f6821f" />
            </div>
            <h2 className="ac-cta-title">Ready to build with AI?</h2>
            <p className="ac-cta-sub">Join {loading ? '...' : `${stats.user_count || 328}+`} developers already running agents on RunAI.</p>
            <div className="ac-cta-btns">
              <Link to="/register" className="ac-btn-primary">Start for free <ArrowRight size={15} /></Link>
              <Link to="/social" className="ac-btn-ghost"><Globe size={14} /> Explore the community</Link>
            </div>
          </div>
        </section>

      </div>
    </MainLayout>
  )
}
